// Firebase configuration and initialization
// Replace these values with your Firebase project config from the Firebase Console:
// Project Settings → General → Your Apps → Firebase SDK snippet → Config
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyAbsB6OVi6bLjBVq6Vr6g2X7rlri16-97Y',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'vivalynlabs-4da03.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'vivalynlabs-4da03',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'vivalynlabs-4da03.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '630256854026',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:630256854026:web:d1f0ed8e919f8857dc6bf3',
};

// Only initialize if config is present
const isConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

let app = null;
let auth = null;

if (isConfigured) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
}

// ── Providers ────────────────────────────────────────────────────────
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({ tenant: 'common' }); // allows any Microsoft/Outlook account
microsoftProvider.addScope('email');
microsoftProvider.addScope('profile');
microsoftProvider.addScope('openid');

// ── Sign-in helpers ──────────────────────────────────────────────────

/**
 * Sign in with Google via popup.
 * Returns the Firebase ID token string for backend verification.
 */
export async function signInWithGoogle() {
  if (!auth) throw new Error('Firebase is not configured. Set REACT_APP_FIREBASE_* env vars.');
  const result = await signInWithPopup(auth, googleProvider);
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email,
    name: result.user.displayName,
    avatar: result.user.photoURL,
    provider: 'google',
  };
}

/**
 * Sign in with Microsoft (Outlook / M365) via popup.
 * Returns the Firebase ID token string for backend verification.
 */
export async function signInWithMicrosoft() {
  if (!auth) throw new Error('Firebase is not configured. Set REACT_APP_FIREBASE_* env vars.');
  const result = await signInWithPopup(auth, microsoftProvider);
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email,
    name: result.user.displayName,
    avatar: result.user.photoURL,
    provider: 'microsoft',
  };
}

/**
 * Sign out from Firebase (clears Firebase session only).
 */
export async function firebaseLogout() {
  if (auth) await firebaseSignOut(auth);
}

// ── Phone OTP helpers ────────────────────────────────────────────────

/**
 * Set up invisible reCAPTCHA on a given button element.
 * Call once before sending OTP. Returns the RecaptchaVerifier instance.
 */
export function setupRecaptcha(buttonId) {
  if (!auth) throw new Error('Firebase is not configured.');
  // Clear any existing verifier
  if (window.recaptchaVerifier) {
    window.recaptchaVerifier.clear();
    window.recaptchaVerifier = null;
  }
  window.recaptchaVerifier = new RecaptchaVerifier(auth, buttonId, {
    size: 'invisible',
  });
  return window.recaptchaVerifier;
}

/**
 * Send OTP to phone number (E.164 format, e.g. "+919876543210").
 * Returns the confirmationResult needed to verify the code.
 */
export async function sendPhoneOtp(phoneNumber) {
  if (!auth) throw new Error('Firebase is not configured.');
  const appVerifier = window.recaptchaVerifier;
  if (!appVerifier) throw new Error('Call setupRecaptcha() first.');
  const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
  return confirmationResult;
}

/**
 * Verify the OTP code entered by the user.
 * Returns the Firebase user credential on success.
 */
export async function verifyPhoneOtp(confirmationResult, code) {
  const result = await confirmationResult.confirm(code);
  // Sign out from Firebase — we only needed phone verification, not a Firebase session
  if (auth) await firebaseSignOut(auth);
  return result;
}

export { auth, isConfigured as isFirebaseConfigured };
